import { toNormalizedMessage } from '@/shared/utils/sentry/clientIgnoreRules/stringMatchers';
import { ClientSentryIgnoreRule } from '@/shared/utils/sentry/clientIgnoreRules/types';

const EXPECTED_ABORT_MESSAGES = [
  'aborterror: the user aborted a request.',
  'the user aborted a request.',
  'aborterror: signal is aborted without reason',
  'signal is aborted without reason',
] as const;

const SELLER_UI_APP_FRAME_MARKERS = [
  'webpack://_n_e/./src/',
  'webpack://_n_e/src/',
  'webpack://_n_e/./pages/',
  'webpack://_n_e/pages/',
  '/_next/static/chunks/',
  '/_next/static/chunks/pages/',
] as const;

const AB_SMARTLY_FRAME_MARKERS = [
  '@absmartly/javascript-sdk',
  'absmartly.min.js',
] as const;

const hasExpectedAbortMessage = (message: unknown): boolean => {
  const normalizedMessage = toNormalizedMessage(message);

  if (!normalizedMessage) {
    return false;
  }

  return EXPECTED_ABORT_MESSAGES.some(
    (expectedMessage) => normalizedMessage === expectedMessage
  );
};

const hasSellerUiAppStackFrame = (stackFrameFilenames: string[]): boolean =>
  stackFrameFilenames.some((filename) => {
    const normalizedFilename = toNormalizedMessage(filename);

    return Boolean(
      normalizedFilename &&
      SELLER_UI_APP_FRAME_MARKERS.some((marker) =>
        normalizedFilename.includes(marker)
      )
    );
  });

const hasABSmartlyStackFrame = (stackFrameFilenames: string[]): boolean =>
  stackFrameFilenames.some((filename) => {
    const normalizedFilename = toNormalizedMessage(filename);

    return Boolean(
      normalizedFilename &&
      AB_SMARTLY_FRAME_MARKERS.some((marker) =>
        normalizedFilename.includes(marker)
      )
    );
  });

export const thirdPartyABSmartlyAbortErrorRule: ClientSentryIgnoreRule = {
  id: 'third-party-absmartly-abort-error',
  references: ['FTR-2766'],
  reason:
    'Known AB Smartly browser abort noise outside seller-ui app frames for triage',
  matches: ({
    firstExceptionType,
    originalExceptionMessage,
    serializedMessage,
    hasExceptionStacktrace,
    stackFrameFilenames,
  }) => {
    if (firstExceptionType !== 'AbortError') {
      return false;
    }

    if (
      !hasExpectedAbortMessage(serializedMessage) &&
      !hasExpectedAbortMessage(originalExceptionMessage)
    ) {
      return false;
    }

    if (hasSellerUiAppStackFrame(stackFrameFilenames)) {
      return false;
    }

    return (
      hasExceptionStacktrace && hasABSmartlyStackFrame(stackFrameFilenames)
    );
  },
};
